import { makeAutoObservable, runInAction } from 'mobx'
import { api } from '@/shared/api/endpoints'
import type {
  ApiKeyResponse, WebhookDeliveryResponse, WebhookEndpointResponse,
} from '@/shared/api/types'
import { explain } from '@/shared/lib/errors'
import { Resource } from '@/stores/lib/Resource'
import type { UiStore } from '@/stores/UiStore'

const PAGE = 20

/**
 * Что показывается ОДИН РАЗ. Ключ API и секрет вебхука бекенд отдаёт
 * только в ответе на выдачу; повторно их не получить. Пока диалог
 * открыт, значение живёт здесь; закрыли — его больше нет нигде.
 */
export type Reveal =
  | { kind: 'none' }
  | { kind: 'key'; name: string; key: string }
  | { kind: 'secret'; url: string; secret: string }

/**
 * Интеграция мерчанта: ключи API, приёмник вебхуков, журнал доставок.
 *
 * Отдельный стор, а не часть AdminStore: живёт на карточке одного
 * аккаунта и перечитывает свои три ресурса после каждого действия.
 * Выдача ключа — не «добавить строку в список», а «показать секрет
 * один раз», поэтому у стора есть состояние показа, которого больше
 * ни у одного экрана нет.
 */
export class IntegrationStore {
  readonly keys: Resource<ApiKeyResponse[]>
  readonly webhook: Resource<WebhookEndpointResponse>
  readonly deliveries: Resource<{ items: WebhookDeliveryResponse[]; total: number }>

  accountId = ''
  deliveriesOffset = 0
  busy = false
  reveal: Reveal = { kind: 'none' }

  // Черновики форм. URL — только при редактировании приёмника.
  keyName = ''
  webhookUrl = ''
  editingWebhook = false

  private readonly ui: UiStore

  constructor(ui: UiStore) {
    this.ui = ui
    this.keys = new Resource(() => api.admin.apiKeys(this.accountId))
    this.webhook = new Resource(() => api.admin.webhook(this.accountId))
    this.deliveries = new Resource(async () => {
      const page = await api.admin.webhookDeliveries(this.accountId, {
        limit: PAGE, offset: this.deliveriesOffset,
      })
      return { items: page.items, total: page.total }
    })
    makeAutoObservable<this, 'ui'>(this, { ui: false })
  }

  get limit(): number {
    return PAGE
  }

  open(accountId: string): void {
    if (this.accountId !== accountId) {
      this.accountId = accountId
      this.deliveriesOffset = 0
      this.reveal = { kind: 'none' }
      this.keyName = ''
      this.webhookUrl = ''
      this.editingWebhook = false
    }
    void this.keys.load()
    void this.webhook.load()
    void this.deliveries.load()
  }

  goDeliveries(offset: number): void {
    this.deliveriesOffset = offset
    void this.deliveries.load()
  }

  setKeyName(value: string): void {
    this.keyName = value
  }

  setWebhookUrl(value: string): void {
    this.webhookUrl = value
  }

  startEditWebhook(): void {
    this.webhookUrl = this.webhook.value?.url ?? ''
    this.editingWebhook = true
  }

  cancelEditWebhook(): void {
    this.editingWebhook = false
  }

  /** Закрыть показ секрета. После этого его не вернуть — так и задумано. */
  closeReveal(): void {
    this.reveal = { kind: 'none' }
  }

  async issueKey(): Promise<void> {
    const name = this.keyName.trim()
    if (!name || this.busy) return
    this.busy = true
    try {
      const issued = await api.admin.issueApiKey(this.accountId, name)
      runInAction(() => {
        this.reveal = { kind: 'key', name: issued.name, key: issued.key }
        this.keyName = ''
      })
      await this.keys.load()
    } catch (cause) {
      this.ui.failure(explain(cause))
    } finally {
      runInAction(() => { this.busy = false })
    }
  }

  async revokeKey(key: ApiKeyResponse): Promise<void> {
    if (this.busy) return
    this.busy = true
    try {
      await api.admin.revokeApiKey(this.accountId, key.id)
      await this.keys.load()
      this.ui.success(`Ключ «${key.name}» отозван`)
    } catch (cause) {
      this.ui.failure(explain(cause))
    } finally {
      runInAction(() => { this.busy = false })
    }
  }

  async saveWebhook(): Promise<void> {
    const url = this.webhookUrl.trim()
    if (!url || this.busy) return
    this.busy = true
    try {
      const set = await api.admin.setWebhook(this.accountId, url)
      runInAction(() => {
        this.reveal = { kind: 'secret', url: set.url ?? url, secret: set.secret }
        this.editingWebhook = false
      })
      await this.webhook.load()
    } catch (cause) {
      this.ui.failure(explain(cause))
    } finally {
      runInAction(() => { this.busy = false })
    }
  }

  async clearWebhook(): Promise<void> {
    if (this.busy) return
    this.busy = true
    try {
      await api.admin.clearWebhook(this.accountId)
      await this.webhook.load()
      this.ui.success('Вебхук отключён')
    } catch (cause) {
      this.ui.failure(explain(cause))
    } finally {
      runInAction(() => { this.busy = false })
    }
  }

  async retry(delivery: WebhookDeliveryResponse): Promise<void> {
    if (this.busy) return
    this.busy = true
    try {
      await api.admin.retryWebhookDelivery(delivery.id)
      await this.deliveries.load()
      this.ui.success('Доставка снова в очереди')
    } catch (cause) {
      this.ui.failure(explain(cause))
    } finally {
      runInAction(() => { this.busy = false })
    }
  }
}
